import React from "react";
import moment from "moment";
import "moment/locale/zh-tw";
import ResponsiveECharts from "./ResponsiveECharts";

// 系列名同時是 tooltip 的分支條件 (只有市場均價那條掛選日期的按鈕)，
// 拉成常數才不會改了圖例卻忘了改判斷式
const MARKET_SERIES = "市場均價";
const SELF_SERIES = "本飯店均價";
// 正常帶是兩條 stack 系列疊出來的，下界那條不進圖例也不吃 tooltip
const BAND_SERIES = "訂房正常帶";
const BAND_LO_SERIES = "__band_lo";

class AvaragePriceTrendChart extends React.Component {
  /**
   * 關掉提示。標記完日期類型後由 DashboardPage 呼叫 —— 提示是 triggerOn:"click"
   * 開的，不會自己消失，標記完還留在畫面上會擋住剛標好的那根長條。
   */
  hideTip = () => {
    if (this.chart) {
      this.chart.dispatchAction({ type: "hideTip" });
    }
  };

  onChartReady = (instance) => {
    this.chart = instance;
  };

  //圖表參數設定
  getChartOption = () => {
    return {
      tooltip: {
        show: true,
        // 軸觸發：點繪圖區任一處都取最近的日期，不必命中那 5.4px 的長條
        trigger: "axis",
        axisPointer: {
          type: "line",
          snap: true,
          lineStyle: { color: "#8c8c8c", width: 1, type: "dashed" },
          label: { show: true, backgroundColor: "#595959" },
        },
        // 點擊才開，不隨滑鼠移動。這個提示掛著選日期的按鈕 (enterable)，滑鼠移動時
        // 若跟著換日期，往提示移動的路上就會一直被換掉 —— 實測 14 步的移動中換了 4 次，
        // 按下去時早就不是原本那一天。改成 click：開了就固定，可以安心移進去按按鈕。
        triggerOn: "click",
        // 底邊貼著點擊處：按鈕列在框的最下方，所以把下緣對齊游標，手移動最短距離就按得到。
        // 左下角優先 (框往右上展開)，右邊放不下就換右下角 (往左上展開)。
        //
        // 座標一律是「容器相對」—— 先前試過 appendToBody + 自算頁面座標，兩套座標混用
        // 怎麼算都對不上 (實測框完全不跟游標動)，已經拿掉。這裡沒有 appendToBody，
        // point / viewSize / 回傳值同一套，單純夾在容器內即可。
        position: (point, params, dom, rect, size) => {
          const [w, h] = size.contentSize;
          const [vw, vh] = size.viewSize;
          const GAP = 6;
          // 水平：左下角對齊 → 框往右展開；右邊放不下就改右下角對齊 → 往左展開
          let x = point[0] - GAP;
          if (x + w > vw) x = point[0] - w + GAP;
          x = Math.max(0, Math.min(x, Math.max(0, vw - w)));
          // 垂直：下緣貼著游標；上方放不下 (點到高長條的頂端) 才改放下方
          let y = point[1] - h - GAP;
          if (y < 0) y = point[1] + GAP;
          y = Math.max(0, Math.min(y, Math.max(0, vh - h)));
          return [x, y];
        },
        backgroundColor: "rgb(255,255,255)",
        borderColor: "#000",
        textStyle: {
          color: "#000",
        },
        enterable: true,
        padding: 0,
        formatter: (params, ticket, callback) => {
          // 軸觸發給的是該日所有系列的陣列；日期與價格一律取市場均價那條
          const list = Array.isArray(params) ? params : [params];
          const market = list.find((p) => p.seriesName === MARKET_SERIES);
          if (!market) return "";
          params = market;
          return `<div class="tooltip-box">
          <div class="title">${moment(params.name).format("M/D")} (${moment(params.name).format("dd")})${this.holidayBadge(params.name)}</div>
          ${this.getMetrics(params.name, params.value, params.data.raw)}
          <div class="buttons">
            <button class="button" ${this.props.dateSetting[params.name] === "normalday"
              ? "data-active"
              : ""
            } onclick={window.AvaragePriceTrend.onDatePricetypeClick(this)} data-date="${params.name}" data-type="normalday">
              平日
            </button>
            <button class="button" ${this.props.dateSetting[params.name] === "peakseasonday"
              ? "data-active"
              : ""
            } onclick={window.AvaragePriceTrend.onDatePricetypeClick(this)} data-date="${params.name}" data-type="peakseasonday">
              旺日
            </button>
            <button class="button" ${this.props.dateSetting[params.name] === "holiday"
              ? "data-active"
              : ""
            } onclick={window.AvaragePriceTrend.onDatePricetypeClick(this)} data-date="${params.name}" data-type="holiday">
              假日
            </button>
            <button class="button" ${this.props.dateSetting[params.name] === "peakseasonholiday"
              ? "data-active"
              : ""
            } onclick={window.AvaragePriceTrend.onDatePricetypeClick(this)} data-date="${params.name}" data-type="peakseasonholiday">
              旺假日
            </button>
            <button class="button" ${this.props.dateSetting[params.name] === "price"
              ? "data-active"
              : ""
            } onclick={window.AvaragePriceTrend.onDatePricetypeClick(this)} data-date="${params.name}" data-type="price">
              定價
            </button>
            <button class="deleteButton" ${this.props.dateSetting[params.name] === "price"
              ? "data-active"
              : ""
            } onclick={window.AvaragePriceTrend.onDatePricetypeClick(this)} data-date="${params.name}" data-type="delete" ${this.props.dateSetting[params.name] ? "" : "disabled"}>
              取消
            </button>
            <button class="wideButton" data-date="${params.name}" onclick={window.AvaragePriceTrend.onDateDetailClick(this)}>顯示明細</button>
          </div>
        </div>`;
        },
      },
      legend: {
        data: [MARKET_SERIES, SELF_SERIES, "市場房型數", "在手 ADR", "在手住房率", BAND_SERIES],
        top: 0,
      },
      grid: {
        left: "60px",
        right: "110px",
        top: "12%",
        bottom: "80px",
      },
      dataZoom: [
        { type: "inside" },
        {
          type: "slider",
          xAxisIndex: [0],
          borderColor: "#eee",
          dataBackground: {
            lineStyle: { color: "rgba(201, 169, 95, 0.9)" },
            areaStyle: { color: "rgba(201, 169, 95, 0.9)" },
          },
          fillerColor: "rgba(87, 38, 0, 0.2)",
          handleSize: "70%",
          handleIcon:
            "M10.7,11.9H9.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4h1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z",
          handleStyle: {
            color: "#fff",
            shadowBlur: 3,
            shadowColor: "rgba(0, 0, 0, 0.5)",
            shadowOffsetX: 2,
            shadowOffsetY: 2,
          },
        },
      ],
      xAxis: [
        {
          type: "category",
          // 指示線掛在軸上、跟著滑鼠走：半年 184 天、每天只有 5.4px，沒有這條線根本
          // 瞄不準會選到哪一天。實測點擊命中率本來就是 100%，問題從不是點不到，
          // 是點不準 —— 缺的是「按下去之前先知道會選到誰」。
          // 注意不要改成全域的 axisPointer.triggerOn: "mousemove"：那會蓋過
          // tooltip.triggerOn:"click"，提示變成隨滑鼠開，移動途中日期又會被換掉。
          axisPointer: {
            show: true,
            type: "line",
            snap: true,
            lineStyle: { color: "#8c8c8c", width: 1, type: "dashed" },
            label: { show: true, backgroundColor: "#595959" },
          },
          data: this.props.avaragePriceTrend.map((item) => item.date),
          axisLabel: {
            formatter: function (value, index) {
              let date = moment(value).format("DD");
              let weekdays = `${moment(value).format("dd")}`;
              let month = `${moment(value).format("MM")}`;
              return `${month}/${date}\n${weekdays}`;
            },
          },
          axisLine: {
            lineStyle: {
              color: "#aaa",
            },
          },
          axisTick: {
            interval: 0,
          },
          scale: true,
        }
      ],
      yAxis: [
        {
          type: "value",
          position: "left",
          scale: true,
          min: 0,
          max: 15000,
          axisLabel: {
            formatter: function (value) {
              let price = "$" + value;
              return price;
            },
          },
          axisLine: {
            lineStyle: {
              color: "#aaa",
            },
          },
        },
        {
          type: "value",
          scale: true,
          name: '市場房型數',
          nameGap: 24,
          min: 0,
        },
        {
          // 住房率是百分比，跟價格與房型數都不同尺度，必須自己一軸
          type: "value",
          name: '在手住房率',
          position: "right",
          offset: 55,
          // 兩個右軸的標題會在頂端重疊，錯開一個字高
          nameGap: 42,
          min: 0,
          max: 100,
          axisLabel: { formatter: "{value}%" },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          xAxisIndex: 0,
          yAxisIndex: 0,
          name: MARKET_SERIES,
          type: "bar",
          itemStyle: {
            color: "#333",
            barBorderRadius: 5,
          },
          data: this.props.avaragePriceTrend,
        },
        {
          // 本飯店與市場同尺度，掛同一軸才看得出價差
          xAxisIndex: 0,
          yAxisIndex: 0,
          name: SELF_SERIES,
          type: "line",
          smooth: true,
          symbol: "none",
          itemStyle: { color: "#F2994A" },
          lineStyle: { width: 2.5 },
          data: this.getSelfPriceData(),
        },
        {
          xAxisIndex: 0,
          yAxisIndex: 1,
          name: "市場房型數",
          type: "line",
          itemStyle: {
            color: "darkred",
            barBorderRadius: 5,
          },
          data: this.getRoomtypeData(),
          markLine: this.getTodayMarkLine(),
        },
        {
          // ADR 與市場均價同尺度，掛同一軸才比得出來
          xAxisIndex: 0,
          yAxisIndex: 0,
          name: "在手 ADR",
          type: "line",
          smooth: true,
          symbol: "none",
          itemStyle: { color: "#2F80ED" },
          lineStyle: { width: 2 },
          data: this.getOpsData("adr"),
        },
        // 正常帶用 echarts 慣用的 stack 疊法：下界透明打底，上面疊「帶寬」並填色。
        // 必須排在住房率線之前，否則會蓋住線。
        {
          xAxisIndex: 0,
          yAxisIndex: 2,
          name: BAND_LO_SERIES,
          type: "line",
          stack: "paceBand",
          smooth: true,
          symbol: "none",
          lineStyle: { opacity: 0 },
          itemStyle: { opacity: 0 },
          silent: true,
          data: this.getBandData("lo"),
        },
        {
          xAxisIndex: 0,
          yAxisIndex: 2,
          name: BAND_SERIES,
          type: "line",
          stack: "paceBand",
          smooth: true,
          symbol: "none",
          lineStyle: { opacity: 0 },
          // 這張圖已經有五條線，帶太淡會直接看不見；實測 0.13 在 6 個月的視野下等於沒畫
          areaStyle: { color: "rgba(39, 174, 96, 0.28)" },
          silent: true,
          data: this.getBandData("width"),
        },
        {
          xAxisIndex: 0,
          yAxisIndex: 2,
          name: "在手住房率",
          type: "line",
          smooth: true,
          symbol: "none",
          itemStyle: { color: "#27AE60" },
          lineStyle: { width: 2, type: "dashed" },
          data: this.getOpsData("occ"),
        },
      ]
    };
  };

  /**
   * 本飯店逐日均價。後端的 averageprice 已排除自家 (見 RoomPriceController::getAveragePrices)，
   * 這條才是「我」，長條是「市場」，兩條的落差就是價格定位。
   * 沒抓到價的日子給 null —— echarts 會斷線而不是掉到 0。
   */
  getSelfPriceData = () =>
    this.props.avaragePriceTrend.map((item) => {
      const p = item.raw ? item.raw.selfprice : null;
      return p === null || p === undefined ? null : Math.round(Number(p));
    });

  /**
   * 訂房正常帶：過去 5 年同 lead time、同星期的在手率 P25–P75。
   * 疊法是 stack —— 下界打底、上面疊帶寬，所以 "width" 回的是 hi−lo 而不是 hi。
   * 只有未來 8 週有資料 (後端窗口)，其餘給 null 讓 echarts 不畫。
   */
  getBandData = (which) =>
    this.props.avaragePriceTrend.map((item) => {
      const b = (this.props.paceBand || {})[item.date];
      if (!b || b.lo === null || b.hi === null) return null;
      return which === "lo" ? b.lo : Math.max(0, Number((b.hi - b.lo).toFixed(1)));
    });

  /**
   * 提示標題後的日曆身分標記。
   *
   * 定價時最需要知道的就是「這天是什麼日子」—— 同樣是週六，春節與普通週末
   * 的訂價完全不同，而補班的週六其實是上班日。判準來自 holidays 表，不是星期幾。
   *
   * 提示是 echarts 的 HTML 字串，用不了 React 元件，所以這裡直接組標籤樣式;
   * 用色與表格裡的 DateWithWeekday 一致。
   */
  /** 標籤最多幾個字。提示的指標表約 240px 寬，扣掉日期後剩下的空間裝得下 8 字。 */
  MAX_BADGE_CHARS = 8;

  holidayBadge = (date) => {
    const day = (this.props.holidayMap || {})[date];
    if (!day) return "";

    const styles = {
      weekend: { bg: "#fff1f0", fg: "#cf1322", border: "#ffa39e" },
      special: { bg: "#fff7e6", fg: "#d46b08", border: "#ffd591" },
      workday: { bg: "#e6f4ff", fg: "#0958d9", border: "#91caff" },
    };
    const style = styles[day.category];
    if (!style) return "";

    const labels = { weekend: "例假日", special: day.name || "特別假日", workday: "補班" };
    const full = String(labels[day.category]);

    // 名稱最長有 15 字 (臺灣光復暨金門古寧頭大捷紀念日)，接在日期後面會撐破提示。
    // 截斷而不是換行 —— 標籤有邊框與底色，斷行會把框切成兩半更難看;
    // 也不縮字級，12px 已經是提示裡最小的字，再小就讀不了。
    // 完整名稱放進原生 title，提示是 enterable，滑鼠進得去看得到。
    const chars = Array.from(full);
    const shown = chars.length > this.MAX_BADGE_CHARS
      ? chars.slice(0, this.MAX_BADGE_CHARS).join("") + "…"
      : full;

    // 名稱來自政府開放資料，仍一律轉義 —— 提示是用字串拼出來的 HTML
    const escape = (value) => String(value).replace(/[&<>"]/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c])
    );

    return `<span title="${escape(full)}" style="margin-left:6px;padding:0 6px;border-radius:3px;`
      + `font-size:12px;white-space:nowrap;background:${style.bg};color:${style.fg};`
      + `border:1px solid ${style.border};">${escape(shown)}</span>`;
  };

  /**
   * 提示裡的指標表：市場 / 本飯店 / 在手三組，標籤靠左、數字靠右對齊。
   *
   * 原本是五行等寬的敘述句，真正要決策的兩個訊號 (與市場的價差、正常帶判讀)
   * 埋在一堆數字裡看不出來。改成分組表格 + 判讀色塊，掃一眼就分得出輕重。
   */
  getMetrics = (date, marketPrice, raw) => {
    const ops = (this.props.opsSeries || {})[date];
    const band = (this.props.paceBand || {})[date];
    const n = (v) => Number(v).toLocaleString();

    const row = (k, v, sub = "") =>
      `<div class="row"><span class="k">${k}</span><span class="v">${v}</span><span class="sub">${sub}</span></div>`;

    const groups = [];

    // 市場 / 本飯店
    const price = [row("市場均價", `$${n(marketPrice)}`, `${raw ? raw.count : 0} 筆`)];
    const self = raw && raw.selfprice !== null && raw.selfprice !== undefined
      ? Math.round(Number(raw.selfprice)) : null;
    if (self !== null) {
      const gap = self - Math.round(Number(marketPrice));
      const arrow = gap >= 0 ? "▲" : "▼";
      price.push(row("本飯店均價", `$${n(self)}`,
        `<span class="gap">${arrow} ${gap >= 0 ? "+" : ""}${n(gap)}</span>`));
    }
    groups.push(`<div class="group">${price.join("")}</div>`);

    // 在手表現
    if (ops) {
      const perf = [];
      if (ops.occ !== null && ops.occ !== undefined) {
        perf.push(row("在手住房率", `${ops.occ}%`, `${ops.sold}/${ops.rooms} 間`));
      }
      if (band && band.lo !== null && ops.occ !== null && ops.occ !== undefined) {
        // 光看在手住房率分不出「賣得差」與「還早」，判讀才是要拿來決定調不調價的
        const cls = ops.occ < band.lo ? "behind" : ops.occ > band.hi ? "ahead" : "normal";
        const text = cls === "behind" ? "落後" : cls === "ahead" ? "超前" : "正常";
        perf.push(row("正常帶", `${band.lo}–${band.hi}%`,
          `<b class="chip ${cls}">${text}</b>`));
        // 一併顯示去年同 lead：「正常」對有成長目標的人是警訊而不是好消息 ——
        // 它的意思是照這個速度會落在跟去年一樣的位置。把那個數字擺出來才不會誤讀
        if (band.ly !== null && band.ly !== undefined) {
          perf.push(row("去年同期", `${band.ly}%`));
        }
      }
      if (ops.adr !== null && ops.adr !== undefined) {
        perf.push(row("在手 ADR", `$${n(ops.adr)}`));
      }
      if (perf.length) groups.push(`<div class="group">${perf.join("")}</div>`);
    }

    return `<div class="metrics">${groups.join("")}</div>`;
  };

  /**
   * 逐日的營運指標。PMS 連不上或該日沒有資料時給 null —— echarts 會斷線而不是畫成 0，
   * 「沒有資料」與「住房率 0%」在畫面上必須分得開。
   */
  getOpsData = (field) => {
    const ops = this.props.opsSeries || {};
    return this.props.avaragePriceTrend.map((item) => {
      const row = ops[item.date];
      return row && row[field] !== undefined && row[field] !== null ? row[field] : null;
    });
  };

  /**
   * 今天的標線。日期區間可以往前後翻，today 不一定在範圍內，不在就不畫。
   */
  getTodayMarkLine = () => {
    const today = moment().format("YYYY-MM-DD");
    const inRange = this.props.avaragePriceTrend.some((item) => item.date === today);
    if (!inRange) return undefined;
    return {
      symbol: "none",
      silent: true,
      label: { formatter: "今天", position: "insideEndTop", color: "#EB5757" },
      lineStyle: { color: "#EB5757", type: "solid", width: 1 },
      data: [{ xAxis: today }],
    };
  };

  getRoomtypeData = () => {
    return this.props.avaragePriceTrend.map((data) => {
      return {
        date: data.date,
        value: data.raw ? parseInt(data.raw.room_count) : 0,
        raw: data.raw
      }
    });
  };
  render() {
    // 包一層只為了讓 tooltip 的定位算式拿得到這張圖自己的 canvas
    // 高度要放得下提示框 (約 277px)。預設 300px 太擠，半年 184 個點也看不清楚。
    return <ResponsiveECharts option={this.getChartOption()} xs={330} lg={380} onChartReady={this.onChartReady} />;
  }
}
export default AvaragePriceTrendChart;
